import React from 'react';

import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Upload from 'antd/es/upload/Upload';

import Button from '@common/react/components/Forms/Button';
import { uploadFile } from '@common/react/components/Forms/Files/File';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';

import { request } from '@app/components/Api';

interface BaseFileProps {
	type: string;
	objectId: number;
	onError?: (error: string) => void;
	fileType?: FileType;
	infoMessage?: string;
	asButton?: boolean;
	accept?: string;
	buttonCaption?: string | JSX.Element;
	buttonClassName?: string;
	errorClassName?: string;
	disabled?: boolean;
	multiple?: boolean;
	noRelation?: boolean; // Whether to create relation for uploaded file or not
	additionalData?: object;
	transformData?: (data: FormData) => FormData; // Transform data before sending to server
	request?: string;
}

interface FileProps extends BaseFileProps {
	update: (data: FileInterface) => void;
	showError?: boolean;
	customRequest?: (params: any) => Promise<any>;
}

interface CustomRequestParams {
	action: any;
	data: any;
	file: any;
	filename: any;
	headers: any;
	onError: any;
	onProgress: any;
	onSuccess: any;
	withCredentials: any;
}

export const File: React.FC<FileProps> = (props) => {
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>('');
	const [uploadButton, setUploadButton] = React.useState<React.ReactNode>(null);

	const {
		buttonCaption = 'Upload',
		buttonClassName,
		showError = true,
		accept = '',
		asButton = false,
		infoMessage = 'Image size must not exceed 1 Mb',
	} = props;

	const customRequest = (argument) => {
		const { file, headers } = argument as CustomRequestParams;

		const {
			type,
			objectId,
			fileType = FileType.Avatar,
			additionalData,
			noRelation,
			transformData,
			request,
		} = props;

		const params = {
			file,
			fileType,
			objectId,
			type,
			additionalData,
			headers,
			request,
		};

		if (noRelation) {
			headers['No-Relation'] = 'true';
		}

		setIsLoading(true);
		setError('');

		(props.customRequest ? props.customRequest(params) : uploadFile(params, transformData))
			.then((data: FileInterface) => {
				props.update(data);
			}).catch((error) => {
				setError(error.message);

				if (props.onError) {
					props.onError(error.message);
				}
			})
			.finally(() => setIsLoading(false));
	};

	React.useEffect(() => {
		if (asButton) {
			setUploadButton(<>
				<Button
					type="button"
					className={buttonClassName}
					isLoading={isLoading}
					disabled={props.disabled}
				>
					{buttonCaption}
				</Button>
				{showError && error.length ? (
					<div
						className={props.errorClassName ? props.errorClassName : 'file-upload-error'}
					>
						{error}
					</div>
				) : null}
			</>);
		} else {
			setUploadButton(<>
				{isLoading ? <BounceLoader /> : <PlusOutlined />}
				<div className="ant-upload-text">{buttonCaption}</div>
				<div className="ant-upload-info">{(error.length && error) || infoMessage}</div>
			</>);
		}
	}, []);

	return (
		<Upload
			className={`avatar-uploader ${error.length ? 'avatar-uploader_error' : ''}`}
			listType={props.asButton ? 'text' : 'picture-card'}
			showUploadList={false}
			accept={accept}
			customRequest={customRequest}
			disabled={props.disabled}
			multiple={props.multiple}
		>
			{uploadButton}
		</Upload>
	);
};

interface CustomFileProps extends BaseFileProps {
	onUploadType: string;
	update?: (data: any) => void;
	onUpload?: (data: any) => void;
}

export const CustomFile: React.FC<CustomFileProps> = (props) => {
	const update = (data: FileInterface) => {
		request(props.onUploadType, {
			fileId: data.id,
		}).then((response: any) => {
			// response cast to any for new typescript support
			const image = {
				...response,
				file: data,
			};

			props.onUpload && props.onUpload(image);
		}).catch((err) => {
			console.log(err);
		});
	};

	return <File update={props.update || update} {...props} />;
};
